import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';

const TutorialContext = createContext(null);

const TUTORIAL_PROJECT = {
  id: 'tutorial-project',
  name: 'Projeto Tutorial (Exemplo)',
};

const defaultSimulated = () => ({
  testCasesByProjectId: {},
  testCyclesByProjectId: {},
  cycleCasesByCycleId: {},
  executionsByProjectId: {},
  evidencesByRunAndCase: {},
  caseStatusByRunAndCase: {},
});

const createJsonResponse = (payload) => {
  const body = JSON.stringify(payload ?? {});
  try {
    return new Response(body, { status: 200, headers: { 'Content-Type': 'application/json' } });
  } catch (_) {
    return { ok: true, status: 200, json: async () => JSON.parse(body) };
  }
};

export const TutorialProvider = ({ children }) => {
  const { user } = useAuth();
  const { showError } = useNotification();

  const [isTutorialMode, setIsTutorialMode] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [simulated, setSimulated] = useState(() => defaultSimulated());

  const originalFetchRef = useRef(null);
  const axiosRequestRef = useRef(null);
  const isTutorialModeRef = useRef(isTutorialMode);
  const fetchWrapperRef = useRef(null);
  const axiosWrapperRef = useRef(null);

  const steps = useMemo(
    () => [
      {
        id: 'overview',
        route: '/overview',
        target: 'start-tutorial',
        title: 'Tutorial (QA)',
        body: 'Clique em “Iniciar tutorial” para ver um fluxo guiado sem salvar nada.',
      },
      {
        id: 'overview-go-repo',
        route: '/overview',
        target: 'nav-test-repository',
        title: 'Repositório de Testes',
        body: 'Vamos para o Repositório de Testes para criar um caso (simulado).',
        advanceOnClick: true,
      },
      {
        id: 'repo-select-project',
        route: '/test-repository',
        target: 'repo-project-select',
        title: 'Selecionar projeto',
        body: 'Selecione um projeto para abrir o repositório.',
        advanceOnChange: true,
      },
      {
        id: 'repo-add-testcase',
        route: '/test-repository',
        target: 'add-caso-teste',
        title: 'Criar caso de teste',
        body: 'Clique para abrir o modal e preencher os campos.',
        advanceOnClick: true,
      },
      {
        id: 'repo-testcase-fields',
        route: '/test-repository',
        target: 'modal-caso-campos',
        title: 'Preencher campos',
        body: 'Preencha os 3 campos: Nome do Caso de Teste, Descrição e Pré-condição. Depois, clique em “Salvar e Fechar”.',
      },
      {
        id: 'repo-go-cycles',
        route: '/test-repository',
        target: 'aba-ciclos',
        title: 'Ciclos de Teste',
        body: 'Agora vamos criar um ciclo (simulado).',
        advanceOnClick: true,
      },
      {
        id: 'repo-new-cycle',
        route: '/test-repository',
        target: 'novo-ciclo',
        title: 'Novo ciclo',
        body: 'Clique para abrir o modal de criação de ciclo.',
        advanceOnClick: true,
      },
      {
        id: 'repo-cycle-fields',
        route: '/test-repository',
        target: 'modal-ciclo-campos',
        title: 'Preencher ciclo',
        body: 'Preencha os 3 campos: Nome do Ciclo de Teste, Descrição e Pré-condição. Depois, clique em “Salvar e Fechar”.',
      },
      {
        id: 'exec-new',
        route: '/executions',
        target: 'nova-execucao',
        title: 'Nova execução',
        body: 'Clique para abrir o modal de criação.',
        advanceOnClick: true,
      },
      {
        id: 'exec-fields',
        route: '/executions',
        target: 'modal-exec-campos',
        title: 'Preencher execução',
        body: 'Preencha os 3 campos: Nome da Execução, Projeto e Ciclo. Depois, clique em “Criar Execução”.',
      },
      {
        id: 'exec-details',
        route: '/executions',
        target: 'ver-detalhes',
        title: 'Detalhes',
        body: 'Abra os detalhes e depois os casos.',
        advanceOnClick: true,
      },
      {
        id: 'exec-view-cases',
        route: '/executions',
        target: 'ver-casos',
        title: 'Ver casos',
        body: 'Abra o modal de casos para simular evidências e status.',
        advanceOnClick: true,
      },
      {
        id: 'case-select',
        route: '/executions',
        target: 'modal-exec-casos-lista',
        title: 'Selecionar caso',
        body: 'No lado esquerdo, selecione o caso que você vai executar. Ao selecionar, os detalhes aparecem à direita.',
        advanceOnClick: true,
      },
      {
        id: 'case-status',
        route: '/executions',
        target: 'modal-exec-casos-status',
        title: 'Status do caso',
        body: 'Aqui você troca o status do caso (ex.: Planejado → Passou/Bug/Melhoria).',
        advanceOnChange: true,
      },
      {
        id: 'case-evidence',
        route: '/executions',
        target: 'modal-exec-casos-evidencia',
        title: 'Evidências',
        body: 'Por último, clique em “+ Adicionar” para anexar uma evidência (imagem/vídeo).',
        advanceOnClick: true,
      },
      {
        id: 'case-save',
        route: '/executions',
        target: 'salvar-caso',
        title: 'Salvar',
        body: 'Salvar aqui não chama API e não grava nada.',
        advanceOnClick: true,
      },
      {
        id: 'finish',
        route: '/overview',
        target: 'start-tutorial',
        title: 'Fim',
        body: 'Finalizar limpa tudo simulado e volta ao modo normal.',
      },
    ],
    []
  );

  const currentStep = steps[stepIndex] || steps[0];

  const start = useCallback(() => {
    const raw = String(user?.role || '').toUpperCase();
    const normalized = raw === 'ADMIN' || raw === 'ADM' ? 'ADM'
      : raw === 'MANAGER' || raw === 'GERENCIAL' ? 'GERENCIAL'
      : raw === 'TESTER' || raw === 'QA' ? 'QA'
      : raw === 'DEVELOPER' || raw === 'DESIGNER' || raw === 'DEV' ? 'DEV'
      : raw === 'VIEWER' || raw === 'CONSULTA' || raw === 'READONLY' ? 'DEV'
      : raw;

    if (normalized !== 'QA') {
      showError('Tutorial disponível apenas para contas QA.');
      return;
    }

    isTutorialModeRef.current = true;
    setSimulated(defaultSimulated());
    setStepIndex(0);
    setIsTutorialMode(true);
  }, [showError, user?.role]);

  const finish = useCallback(() => {
    try {
      localStorage.removeItem('selectedProjectId');
    } catch (_) {}
    try {
      localStorage.removeItem('executionFilters');
    } catch (_) {}
    isTutorialModeRef.current = false;
    setIsTutorialMode(false);
    setStepIndex(0);
    setSimulated(defaultSimulated());
  }, []);

  const next = useCallback(() => {
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  }, [steps.length]);

  const prev = useCallback(() => {
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const addSimulatedTestCase = useCallback((projectId, testCase) => {
    if (!projectId || !testCase) return;
    setSimulated((prevState) => {
      const current = prevState.testCasesByProjectId[String(projectId)] || [];
      return {
        ...prevState,
        testCasesByProjectId: {
          ...prevState.testCasesByProjectId,
          [String(projectId)]: [testCase, ...current],
        },
      };
    });
  }, []);

  const addSimulatedTestCycle = useCallback((projectId, cycle) => {
    if (!projectId || !cycle) return;
    setSimulated((prevState) => {
      const current = prevState.testCyclesByProjectId[String(projectId)] || [];
      return {
        ...prevState,
        testCyclesByProjectId: {
          ...prevState.testCyclesByProjectId,
          [String(projectId)]: [cycle, ...current],
        },
      };
    });
  }, []);

  const setSimulatedCycleCases = useCallback((cycleId, casesList) => {
    if (!cycleId) return;
    const key = String(cycleId);
    const normalized = Array.isArray(casesList) ? casesList : [];
    setSimulated((prevState) => ({
      ...prevState,
      cycleCasesByCycleId: {
        ...prevState.cycleCasesByCycleId,
        [key]: normalized,
      },
    }));
  }, []);

  const getSimulatedCycleCases = useCallback(
    (cycleId) => {
      if (!cycleId) return [];
      return simulated.cycleCasesByCycleId[String(cycleId)] || [];
    },
    [simulated.cycleCasesByCycleId]
  );

  const addSimulatedExecution = useCallback((projectId, execution) => {
    if (!projectId || !execution) return;
    setSimulated((prevState) => {
      const current = prevState.executionsByProjectId[String(projectId)] || [];
      return {
        ...prevState,
        executionsByProjectId: {
          ...prevState.executionsByProjectId,
          [String(projectId)]: [execution, ...current],
        },
      };
    });
  }, []);

  const updateSimulatedExecution = useCallback((projectId, executionId, patch) => {
    if (!projectId || !executionId) return;
    setSimulated((prevState) => {
      const key = String(projectId);
      const list = prevState.executionsByProjectId[key] || [];
      const next = list.map((e) => (String(e?.id) === String(executionId) ? { ...e, ...(patch || {}) } : e));
      return {
        ...prevState,
        executionsByProjectId: {
          ...prevState.executionsByProjectId,
          [key]: next,
        },
      };
    });
  }, []);

  const getSimulatedExecutions = useCallback(
    (projectId) => {
      if (!projectId) {
        return Object.values(simulated.executionsByProjectId || {}).flat();
      }
      return simulated.executionsByProjectId[String(projectId)] || [];
    },
    [simulated.executionsByProjectId]
  );

  const addSimulatedEvidence = useCallback((runId, caseId, evidence) => {
    if (!runId || !caseId || !evidence) return;
    const key = `${String(runId)}::${String(caseId)}`;
    setSimulated((prevState) => {
      const current = prevState.evidencesByRunAndCase[key] || [];
      return {
        ...prevState,
        evidencesByRunAndCase: {
          ...prevState.evidencesByRunAndCase,
          [key]: [evidence, ...current],
        },
      };
    });
  }, []);

  const removeSimulatedEvidence = useCallback((runId, caseId, evidenceId) => {
    const key = `${String(runId)}::${String(caseId)}`;
    setSimulated((prevState) => {
      const current = prevState.evidencesByRunAndCase[key] || [];
      return {
        ...prevState,
        evidencesByRunAndCase: {
          ...prevState.evidencesByRunAndCase,
          [key]: current.filter((e) => String(e.id) !== String(evidenceId)),
        },
      };
    });
  }, []);

  const getSimulatedTestCases = useCallback(
    (projectId) => {
      if (!projectId) {
        return Object.values(simulated.testCasesByProjectId || {}).flat();
      }
      return simulated.testCasesByProjectId[String(projectId)] || [];
    },
    [simulated.testCasesByProjectId]
  );

  const getSimulatedTestCycles = useCallback(
    (projectId) => {
      if (!projectId) {
        return Object.values(simulated.testCyclesByProjectId || {}).flat();
      }
      return simulated.testCyclesByProjectId[String(projectId)] || [];
    },
    [simulated.testCyclesByProjectId]
  );

  const getSimulatedEvidences = useCallback(
    (runId, caseId) => {
      const key = `${String(runId)}::${String(caseId)}`;
      return simulated.evidencesByRunAndCase[key] || [];
    },
    [simulated.evidencesByRunAndCase]
  );

  const setSimulatedCaseStatus = useCallback((runId, caseId, status, patch = {}) => {
    if (!runId || !caseId || !status) return;
    const key = `${String(runId)}::${String(caseId)}`;
    setSimulated((prevState) => ({
      ...prevState,
      caseStatusByRunAndCase: {
        ...prevState.caseStatusByRunAndCase,
        [key]: { status, ...(patch || {}) },
      },
    }));
  }, []);

  const getSimulatedStatusByCaseId = useCallback(
    (runId) => {
      if (!runId) return {};
      const prefix = `${String(runId)}::`;
      const entries = simulated.caseStatusByRunAndCase || {};
      const map = {};
      Object.entries(entries).forEach(([key, value]) => {
        if (!key.startsWith(prefix)) return;
        const caseId = key.slice(prefix.length);
        if (!caseId) return;
        map[String(caseId)] = value;
      });
      return map;
    },
    [simulated.caseStatusByRunAndCase]
  );

  useEffect(() => {
    isTutorialModeRef.current = isTutorialMode;
  }, [isTutorialMode]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!originalFetchRef.current) originalFetchRef.current = window.fetch;

      if (!fetchWrapperRef.current) {
        fetchWrapperRef.current = async (input, init = {}) => {
          const originalFetch = originalFetchRef.current;
          if (!originalFetch) {
            return createJsonResponse({ success: false, error: 'fetch_unavailable' });
          }

          if (!isTutorialModeRef.current) {
            return originalFetch(input, init);
          }

          const methodFromInit = String(init?.method || '').toUpperCase();
          const methodFromRequest = (() => {
            try {
              if (input && typeof input === 'object' && 'method' in input) return String(input.method || '').toUpperCase();
            } catch (_) {}
            return '';
          })();
          const method = methodFromInit || methodFromRequest || 'GET';
          const allowed = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
          if (!allowed) {
            return createJsonResponse({ success: true, tutorial: true });
          }

          const url = (() => {
            try {
              if (typeof input === 'string') return input;
              if (input && typeof input === 'object' && 'url' in input) return String(input.url || '');
            } catch (_) {}
            return '';
          })();

          const isTutorialProject = /project_id=tutorial-project(\b|&|$)/.test(String(url || ''));
          const isProjectsEndpoint = /\/projects(\?|$)/.test(String(url || ''));
          const isRepoTestCases = /\/test-repository\/test-cases(\?|$)/.test(String(url || ''));
          const isRepoTestCycles = /\/test-repository\/test-cycles(\?|$)/.test(String(url || ''));
          const isRepoFolders = /\/test-repository\/folders(\?|$)/.test(String(url || ''));
          const isLegacyTestCasesByProject = /\/testcases\?/.test(String(url || ''));

          if (isProjectsEndpoint) {
            if (isTutorialModeRef.current) {
              return createJsonResponse({ success: true, projects: [TUTORIAL_PROJECT] });
            }
            try {
              const res = await originalFetch(input, init);
              let payload = {};
              try {
                payload = await res.json();
              } catch (_) {
                payload = {};
              }
              const list = Array.isArray(payload?.projects)
                ? payload.projects
                : (Array.isArray(payload) ? payload : []);
              const byId = new Map();
              [TUTORIAL_PROJECT, ...list].forEach((p) => {
                if (!p?.id) return;
                byId.set(String(p.id), p);
              });
              return createJsonResponse({ ...payload, success: true, projects: Array.from(byId.values()) });
            } catch (_) {
              return createJsonResponse({ success: true, projects: [TUTORIAL_PROJECT] });
            }
          }

          if (isTutorialProject && (isRepoTestCases || isRepoTestCycles || isRepoFolders)) {
            return createJsonResponse({ success: true, data: [] });
          }

          if (isTutorialProject && isLegacyTestCasesByProject) {
            return createJsonResponse({ success: true, testCases: [] });
          }

          return originalFetch(input, init);
        };
      }

      window.fetch = fetchWrapperRef.current;
    }

    if (!axiosRequestRef.current) {
      axiosRequestRef.current = axios.request.bind(axios);
    }

    if (!axiosWrapperRef.current) {
      axiosWrapperRef.current = async (config) => {
        const originalRequest = axiosRequestRef.current;
        if (!originalRequest) {
          return {
            data: { success: false, error: 'axios_unavailable' },
            status: 500,
            statusText: 'Error',
            headers: {},
            config,
            request: {},
          };
        }

        if (!isTutorialModeRef.current) {
          return originalRequest(config);
        }

        const method = String(config?.method || 'get').toUpperCase();
        const allowed = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
        if (!allowed) {
          return {
            data: { success: true, tutorial: true },
            status: 200,
            statusText: 'OK',
            headers: {},
            config,
            request: {},
          };
        }

        return originalRequest(config);
      };
    }

    axios.request = axiosWrapperRef.current;

    return () => {
      if (typeof window !== 'undefined' && originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
      }
      if (axiosRequestRef.current) {
        axios.request = axiosRequestRef.current;
      }
      originalFetchRef.current = null;
      axiosRequestRef.current = null;
      fetchWrapperRef.current = null;
      axiosWrapperRef.current = null;
    };
  }, []);

  const value = useMemo(
    () => ({
      isTutorialMode,
      tutorialProjectId: TUTORIAL_PROJECT.id,
      steps,
      currentStep,
      currentStepId: currentStep?.id,
      stepIndex,
      start,
      finish,
      next,
      prev,
      addSimulatedTestCase,
      addSimulatedTestCycle,
      setSimulatedCycleCases,
      getSimulatedCycleCases,
      addSimulatedExecution,
      updateSimulatedExecution,
      getSimulatedExecutions,
      addSimulatedEvidence,
      removeSimulatedEvidence,
      getSimulatedTestCases,
      getSimulatedTestCycles,
      getSimulatedEvidences,
      setSimulatedCaseStatus,
      getSimulatedStatusByCaseId,
    }),
    [
      isTutorialMode,
      
      steps,
      currentStep,
      stepIndex,
      start,
      finish,
      next,
      prev,
      addSimulatedTestCase,
      addSimulatedTestCycle,
      setSimulatedCycleCases,
      getSimulatedCycleCases,
      addSimulatedExecution,
      updateSimulatedExecution,
      getSimulatedExecutions,
      addSimulatedEvidence,
      removeSimulatedEvidence,
      getSimulatedTestCases,
      getSimulatedTestCycles,
      getSimulatedEvidences,
      setSimulatedCaseStatus,
      getSimulatedStatusByCaseId,
    ]
  );

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
};

export const useTutorial = () => {
  const ctx = useContext(TutorialContext);
  return ctx;
};
